import React, { useState, useEffect } from 'react';
import { leavesAPI, holidaysAPI } from '../services/api';
import toast from 'react-hot-toast';
import { 
  Calendar, 
  Clock, 
  FileText, 
  Plus, 
  Users,
  AlertCircle,
  CheckCircle2,
  XCircle,
  CalendarCheck
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LeaveApplication = () => {
  const [activeTab, setActiveTab] = useState('apply');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [weekendDays, setWeekendDays] = useState([]);
  const [formData, setFormData] = useState({
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    reason: '',
    comments: ''
  });

  useEffect(() => {
    loadLeaveTypes();
    loadLeaveBalances();
    loadApplications();
    loadHolidaysAndWeekends();
  }, []);

  const loadLeaveTypes = async () => {
    try {
      const response = await leavesAPI.getLeaveTypes();
      setLeaveTypes(response.data);
    } catch (error) {
      toast.error('Failed to load leave types');
    }
  };

  const loadLeaveBalances = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await leavesAPI.getMyLeaveBalances(currentYear);
      setLeaveBalances(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setLeaveBalances([]);
    }
  };

  const loadApplications = async () => {
    try {
      const response = await leavesAPI.getMyLeaveApplications();
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    }
  };

  const loadHolidaysAndWeekends = async () => {
    try {
      const [holidaysRes, weekendRes] = await Promise.all([
        holidaysAPI.getAllHolidays(),
        holidaysAPI.getWeekendConfig()
      ]);
      setHolidays(holidaysRes.data?.data || holidaysRes.data || []);
      setWeekendDays(weekendRes.data?.weekendDays || []);
    } catch (error) {
      console.error('Failed to load holidays/weekends:', error);
    }
  };

  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return { totalDays: 0, workingDays: 0, weekends: 0, publicHolidays: 0 };
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    let totalDays = 0;
    let weekends = 0;
    let publicHolidays = 0;
    
    const holidayDates = new Set(
      holidays.map(h => new Date(h.date).toISOString().split('T')[0])
    );
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      totalDays++;
      
      const dayOfWeek = date.getDay();
      const dateStr = date.toISOString().split('T')[0];
      
      // Check if it's a weekend
      if (weekendDays.includes(dayOfWeek)) {
        weekends++;
      }
      // Check if it's a holiday (but not already counted as weekend)
      else if (holidayDates.has(dateStr)) {
        publicHolidays++;
      }
    }
    
    const workingDays = totalDays - weekends - publicHolidays;
    
    return { totalDays, workingDays, weekends, publicHolidays };
  };

  const handleApplyLeave = () => {
    setFormData({
      leaveTypeId: '',
      startDate: '',
      endDate: '', // Reset end date too
      reason: '',
      comments: ''
    });
    setShowModal(true);
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault(); // Add preventDefault
    try {
      if (!formData.leaveTypeId || !formData.startDate || !formData.endDate || !formData.reason) {
        toast.error('Please fill all required fields');
        return;
      }

      if (new Date(formData.endDate) < new Date(formData.startDate)) {
        toast.error('End date cannot be before start date');
        return;
      }

      const daysCalc = calculateWorkingDays(formData.startDate, formData.endDate);
      
      if (daysCalc.workingDays <= 0) {
        toast.error('Leave period must include at least one working day');
        return;
      }

      // Check balance before submitting
      const balance = leaveBalances.find(lb => {
        const balanceLeaveTypeId = lb.leaveTypeId?._id || lb.leaveTypeId;
        return balanceLeaveTypeId === formData.leaveTypeId;
      });
      
      if (balance) {
        const available = balance.totalDays - balance.usedDays - balance.pendingDays;
        if (daysCalc.workingDays > available) {
          toast.error(`Insufficient leave balance. You have ${available} working days available but requested ${daysCalc.workingDays} working days.`);
          return;
        }
      }

      await leavesAPI.createLeaveApplication(formData);
      toast.success('Leave application submitted successfully');
      setShowModal(false);
      loadApplications();
      loadLeaveBalances(); // Refresh balances after application
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit application');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'cancelled': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'cancelled': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const selectedLeaveType = leaveTypes.find(lt => lt._id === formData.leaveTypeId);
  const daysCalculation = calculateWorkingDays(formData.startDate, formData.endDate);
  const totalDays = daysCalculation.totalDays;
  const workingDays = daysCalculation.workingDays;
  
  // Get current balance for selected leave type
  const currentBalance = leaveBalances.find(lb => {
    const balanceLeaveTypeId = lb.leaveTypeId?._id || lb.leaveTypeId;
    return balanceLeaveTypeId === formData.leaveTypeId;
  });
  
  const availableDays = currentBalance 
    ? currentBalance.totalDays - currentBalance.usedDays - currentBalance.pendingDays 
    : 0;
  
  const hasInsufficientBalance = workingDays > availableDays && formData.leaveTypeId && workingDays > 0;

  const tabs = [
    { id: 'apply', name: 'Dashboard', icon: CalendarCheck },
    { id: 'history', name: 'My Applications', icon: FileText }
    // Merged Balances into Dashboard/Apply for cleaner UI
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">
            Leave Portal
          </h1>
          <p className="text-sm text-gray-500">Manage your leaves and view balances</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleApplyLeave}
          className="w-full md:w-auto px-4 py-2.5 bg-gray-900 text-white rounded-md shadow-lg hover:bg-gray-800 transition-colors inline-flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Apply New Leave
        </motion.button>
      </div>

      {/* Modern Tabs */}
      <div className="flex w-full overflow-x-auto p-1 bg-white/50 backdrop-blur-sm rounded-md border border-white/20 shadow-sm sm:w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              relative flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap
              ${activeTab === tab.id ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}
            `}
          >
            {activeTab === tab.id && (
              <motion.div
                layoutId="activeTab"
                className="absolute inset-0 bg-gray-100 rounded-lg -z-10"
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
            <tab.icon className="w-4 h-4" />
            {tab.name}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'apply' ? (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {/* Balance Cards */}
            {leaveTypes.map((leaveType) => {
              const balance = leaveBalances.find(lb => {
                const balanceLeaveTypeId = lb.leaveTypeId?._id || lb.leaveTypeId;
                return balanceLeaveTypeId === leaveType._id;
              }) || { totalDays: 0, usedDays: 0, pendingDays: 0 };
              
              const available = balance.totalDays - balance.usedDays - balance.pendingDays;
              const percentage = Math.min(100, (balance.usedDays / balance.totalDays) * 100) || 0;

              return (
                <motion.div
                  key={leaveType._id}
                  whileHover={{ y: -4 }}
                  className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div 
                      className="p-3 rounded-md bg-opacity-10"
                      style={{ backgroundColor: leaveType.color + '20' }}
                    >
                      <div 
                        className="w-6 h-6 rounded-lg"
                        style={{ backgroundColor: leaveType.color }}
                      ></div>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-bold text-gray-800">{available}</span>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Days Available</p>
                    </div>
                  </div>

                  <h3 className="font-semibold text-gray-900 mb-1">{leaveType.name}</h3>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{leaveType.description}</p>

                  <div className="space-y-3">
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: leaveType.color }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Used: {balance.usedDays}</span>
                      <span>Total: {balance.totalDays}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            key="history"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
          >
            {/* Mobile cards */}
            <div className="divide-y divide-gray-100 sm:hidden">
              {applications.length > 0 ? (
                applications.map((app) => (
                  <div key={app._id} className="px-3 py-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: app.leaveTypeId?.color }}
                        />
                        <span className="truncate text-sm font-medium text-gray-900">
                          {app.leaveTypeId?.name}
                        </span>
                      </div>
                      <span className={`inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${getStatusColor(app.status)}`}>
                        {getStatusIcon(app.status)}
                        <span className="capitalize">{app.status}</span>
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                      <span>
                        {new Date(app.startDate).toLocaleDateString()} – {new Date(app.endDate).toLocaleDateString()}
                      </span>
                      <span>{app.totalDays} days</span>
                      <span>Applied {new Date(app.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-12 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  No applications found
                </div>
              )}
            </div>

            {/* Desktop table */}
            <div className="table-scroll hidden sm:block">
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50/50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Leave Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Dates</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Applied On</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {applications.length > 0 ? (
                    applications.map((app) => (
                      <tr key={app._id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: app.leaveTypeId?.color }}
                            ></div>
                            <span className="font-medium text-gray-900">{app.leaveTypeId?.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col text-sm">
                            <span className="text-gray-900 font-medium">
                              {new Date(app.startDate).toLocaleDateString()}
                            </span>
                            <span className="text-gray-500 text-xs">
                              to {new Date(app.endDate).toLocaleDateString()}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {app.totalDays} days
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(app.status)}`}>
                            {getStatusIcon(app.status)}
                            <span className="capitalize">{app.status}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(app.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        No applications found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Application Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Modal Header */}
              <div className="bg-gray-900 px-4 py-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">New Leave Application</h3>
                    <p className="text-emerald-100 text-xs">Fill in the details below</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable Form Content */}
              <div className="overflow-y-auto flex-1 px-4 py-3">
                <form onSubmit={handleSubmitApplication} className="space-y-4" id="leave-application-form">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Leave Type *
                  </label>
                  <select
                    value={formData.leaveTypeId}
                    onChange={(e) => setFormData({ ...formData, leaveTypeId: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-md focus:border-gray-200 focus:ring-4 focus:ring-gray-200 transition-all bg-white"
                    required
                  >
                    <option value="">Select a leave type...</option>
                    {leaveTypes.map((type) => (
                      <option key={type._id} value={type._id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-2">Start Date *</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-md focus:border-gray-200 focus:ring-4 focus:ring-gray-200 transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-2">End Date *</label>
                    <input
                      type="date"
                      value={formData.endDate}
                      min={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-md focus:border-gray-200 focus:ring-4 focus:ring-gray-200 transition-all"
                      required
                    />
                  </div>
                </div>

                {/* Duration Summary */}
                {formData.startDate && formData.endDate && (
                  <div className="bg-gray-50 p-3 rounded-md border-2 border-gray-200 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-4 h-4 text-gray-700" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] text-gray-700 font-medium uppercase tracking-wide">Leave Duration</p>
                        <p className="text-base font-semibold text-gray-700">{workingDays} working days</p>
                      </div>
                    </div>
                    
                    {totalDays > workingDays && (
                      <div className="bg-white/60 backdrop-blur-sm rounded-lg p-2 space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-slate-600 font-medium">Total calendar days:</span>
                          <span className="font-semibold text-gray-900">{totalDays}</span>
                        </div>
                        {daysCalculation.weekends > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-600 font-medium">Weekends:</span>
                            <span className="font-bold text-gray-700">-{daysCalculation.weekends}</span>
                          </div>
                        )}
                        {daysCalculation.publicHolidays > 0 && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-600 font-medium">Public holidays:</span>
                            <span className="font-bold text-gray-700">-{daysCalculation.publicHolidays}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center text-xs pt-1 border-t border-slate-200">
                          <span className="text-gray-900 font-semibold">Working days to deduct:</span>
                          <span className="font-bold text-gray-700 text-sm">{workingDays}</span>
                        </div>
                      </div>
                    )}
                    
                    {workingDays <= 0 && (
                      <div className="flex items-center gap-2 text-xs text-gray-900 bg-gray-100 px-3 py-2 rounded-lg border border-gray-200">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>Selected period contains no working days</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Balance Warning */}
                {formData.leaveTypeId && currentBalance && (
                  <div className={`p-3 rounded-md border-2 ${
                      hasInsufficientBalance 
                      ? 'bg-red-50 border-red-200' 
                      : 'bg-gray-100 border-gray-200'
                  }`}>
                    <div className="flex items-start gap-2">
                      <div className={`p-1.5 rounded-lg ${
                        hasInsufficientBalance ? 'bg-red-100' : 'bg-gray-100'
                      }`}>
                        <Users className={`w-4 h-4 ${
                          hasInsufficientBalance ? 'text-red-600' : 'text-gray-700'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] font-semibold uppercase tracking-wide mb-1 text-slate-600">
                          {selectedLeaveType?.name} Balance
                        </p>
                        <div className="space-y-0.5">
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-600">Available:</span>
                            <span className={`font-bold ${
                              hasInsufficientBalance ? 'text-red-700' : 'text-gray-900'
                            }`}>{availableDays} working days</span>
                          </div>
                          {workingDays > 0 && (
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-slate-600">Requesting:</span>
                              <span className="font-semibold text-gray-900">{workingDays} working days</span>
                            </div>
                          )}
                          {hasInsufficientBalance && (
                            <div className="flex items-center gap-1.5 text-[10px] text-red-700 bg-red-100 px-2 py-1 rounded-lg mt-1.5">
                              <AlertCircle className="w-3 h-3 flex-shrink-0" />
                              <span className="font-medium">
                                Need {workingDays - availableDays} more working days.
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Reason *
                  </label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-md focus:border-gray-200 focus:ring-4 focus:ring-gray-200 transition-all resize-none"
                    rows="2"
                    placeholder="Briefly explain the reason for your leave..."
                    required
                  ></textarea>
                </div>
                </form>
              </div>

              {/* Fixed Footer */}
              <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 flex-shrink-0">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-100 font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="leave-application-form"
                    disabled={hasInsufficientBalance || workingDays <= 0}
                    className={`flex-1 px-6 py-3 rounded-md font-semibold shadow-lg transition-all flex items-center justify-center gap-2 ${
                      hasInsufficientBalance || workingDays <= 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-900 text-white hover:bg-gray-800 shadow-none'
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Submit Application
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeaveApplication;
